import React, { useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Calendar, ExternalLink } from "lucide-react";
import { Helmet } from "react-helmet-async";

import { useSiteContentApi } from "@/hooks/useSiteContentApi";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import SiteHeader from "@/components/SiteHeader";

export default function BlogPostPage() {
  const { slug } = useParams();
  const { content, loading } = useSiteContentApi();
  const navigate = useNavigate();
  
  // Navigation handler - must be before any conditional returns
  const handleNav = useCallback((id) => {
    if (id === "blog") {
      navigate("/blog");
      return;
    }
    navigate("/");
    setTimeout(() => {
      const el = document.getElementById(id);
      if (el) {
        const headerOffset = 80;
        const elementPosition = el.getBoundingClientRect().top;
        const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
        window.scrollTo({ top: offsetPosition, behavior: "smooth" });
      }
    }, 100);
  }, [navigate]);
  
  // Show loading state while content is being fetched
  if (loading || !content) {
    return (
      <div className="min-h-screen bg-[#FAF9F5] flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-[#D8CCE3]/30 border-t-[#D8CCE3]" />
          <div className="mt-4 text-[#333333]/60">Загрузка...</div>
        </div>
      </div>
    );
  }
  
  const { brand, blog, nav } = content;
  const post = (blog?.posts || []).find((p) => p.slug === slug && p.published);
  
  // Navigation with blog always included
  const navWithBlog = [...(nav || []), { id: "blog", label: "БЛОГ", isLink: true }];

  if (!post) {
    return (
      <div className="min-h-screen bg-[#FAF9F5]">
        <SiteHeader brand={brand} nav={navWithBlog} onNav={handleNav} activeSection="blog" />
        <main className="mx-auto max-w-6xl px-5 py-10">
          <Link to="/blog">
            <Button variant="outline" className="border-[#E6E1D7] bg-white text-[#333333] hover:bg-[#FAF9F5]">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Назад к блогу
            </Button>
          </Link>

          <Card className="mt-10 border-[#E6E1D7] bg-white p-8 text-center shadow-sm">
            <div className="text-lg font-medium text-[#333333]">Статья не найдена</div>
            <div className="mt-2 text-[#4A4A4A]">
              Возможно, она была удалена или ещё не опубликована
            </div>
          </Card>
        </main>
      </div>
    );
  }

  const photos = (post.photos || []).filter(Boolean);
  const socialLinks = post.socialLinks || {};
  const hasSocialLinks = Object.values(socialLinks).some(Boolean);

  return (
    <div className="min-h-screen bg-[#FAF9F5]">
      <Helmet>
        <title>{post.title} | Квартирный стилист</title>
        <meta name="description" content={post.excerpt || post.title} />
        <link rel="canonical" href={`https://myhomestaging.ru/blog/${post.slug}`} />
        <meta property="og:title" content={`${post.title} | Квартирный стилист`} />
        <meta property="og:description" content={post.excerpt || post.title} />
        <meta property="og:url" content={`https://myhomestaging.ru/blog/${post.slug}`} />
        <meta property="og:image" content={(post.photos || [])[0] || "https://myhomestaging.ru/og-image.jpg"} />
        <meta property="og:type" content="article" />
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "BlogPosting",
          "headline": post.title,
          "description": post.excerpt || post.title,
          "image": (post.photos || [])[0] || "https://myhomestaging.ru/og-image.jpg",
          "datePublished": post.publishedAt,
          "url": `https://myhomestaging.ru/blog/${post.slug}`,
          "author": {"@type": "Person", "name": "Оксана Фурсова"},
          "publisher": {"@type": "Organization", "name": "Квартирный стилист", "url": "https://myhomestaging.ru"}
        })}</script>
      </Helmet>
      <SiteHeader brand={brand} nav={navWithBlog} onNav={handleNav} activeSection="blog" />
      
      <main className="mx-auto max-w-4xl px-5 py-10">
        {/* Back link */}
        <Link to="/blog">
          <Button variant="outline" className="border-[#E6E1D7] bg-white text-[#333333] hover:bg-[#FAF9F5]">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Назад к блогу
          </Button>
        </Link>

        {/* Article */}
        <article className="mt-8">
          <h1 className="font-display text-3xl tracking-tight text-[#333333] sm:text-4xl lg:text-5xl">
            {post.title}
          </h1>

          <div className="mt-4 flex items-center gap-2 text-[#4A4A4A]/60">
            <Calendar className="h-4 w-4" />
            <span className="text-sm">
              {post.publishedAt ? new Date(post.publishedAt).toLocaleDateString("ru-RU", {
                year: "numeric",
                month: "long",
                day: "numeric",
              }) : "Без даты"}
            </span>
          </div>

          {/* Photos gallery */}
          {photos.length > 0 && (
            <div className="mt-6 grid gap-3 sm:grid-cols-2">
              {photos.map((url, idx) => (
                <div key={idx} className="overflow-hidden border border-[#E6E1D7]">
                  <img src={url} alt={`Фото ${idx + 1}`} className="h-64 w-full object-cover" />
                </div>
              ))}
            </div>
          )}

          <Card className="mt-8 border-[#E6E1D7] bg-white p-6 lg:p-8 shadow-sm">
            <div className="prose prose-lg max-w-none text-[#4A4A4A]">
              {post.content.split("\n\n").map((paragraph, idx) => (
                <p key={idx} className="mb-4 leading-relaxed whitespace-pre-line">
                  {paragraph}
                </p>
              ))}
            </div>

            {/* Social links */}
            {hasSocialLinks && (
              <div className="mt-6 pt-6 border-t border-[#E6E1D7]">
                <div className="text-sm text-[#4A4A4A]/70">Поделиться:</div>
                <div className="mt-3 flex flex-wrap gap-2">
                  {socialLinks.telegram && (
                    <a href={socialLinks.telegram} target="_blank" rel="noopener noreferrer">
                      <Button variant="outline" size="sm" className="border-[#E6E1D7] bg-white text-[#333333] hover:bg-[#FAF9F5]">
                        Telegram <ExternalLink className="ml-1 h-3 w-3" />
                      </Button>
                    </a>
                  )}
                  {socialLinks.whatsapp && (
                    <a href={socialLinks.whatsapp} target="_blank" rel="noopener noreferrer">
                      <Button variant="outline" size="sm" className="border-[#E6E1D7] bg-white text-[#333333] hover:bg-[#FAF9F5]">
                        WhatsApp <ExternalLink className="ml-1 h-3 w-3" />
                      </Button>
                    </a>
                  )}
                  {socialLinks.instagram && (
                    <a href={socialLinks.instagram} target="_blank" rel="noopener noreferrer">
                      <Button variant="outline" size="sm" className="border-[#E6E1D7] bg-white text-[#333333] hover:bg-[#FAF9F5]">
                        Instagram <ExternalLink className="ml-1 h-3 w-3" />
                      </Button>
                    </a>
                  )}
                  {socialLinks.vk && (
                    <a href={socialLinks.vk} target="_blank" rel="noopener noreferrer">
                      <Button variant="outline" size="sm" className="border-[#E6E1D7] bg-white text-[#333333] hover:bg-[#FAF9F5]">
                        VK <ExternalLink className="ml-1 h-3 w-3" />
                      </Button>
                    </a>
                  )}
                </div>
              </div>
            )}
          </Card>
        </article>

        {/* Footer */}
        <footer className="mt-14 border-t border-[#E6E1D7] py-8">
          <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
            <div>
              <div className="text-sm font-semibold text-[#333333]">{brand?.name}</div>
              <div className="mt-1 text-xs text-[#4A4A4A]/55">© {new Date().getFullYear()}</div>
            </div>
            <Link to="/">
              <Button variant="outline" className="border-[#E6E1D7] bg-white text-[#333333] hover:bg-[#FAF9F5]">
                На главную
              </Button>
            </Link>
          </div>
        </footer>
      </main>
    </div>
  );
}
